import { useAuthStore } from '@/features/authentication/stores/authStore';
import { identifyUser, resetAnalytics } from '@/shared/analytics/posthog';
import { useEffect, useRef } from 'react';

/**
 * Binds the analytics identity to the auth session. On transition to
 * `authenticated`, identifies the caller with the Firebase `uid` as distinct id
 * and `email` / `displayName` as person properties; on `unauthenticated`,
 * resets to a fresh anonymous identity so events aren't cross-attributed.
 *
 * Only uid + email/displayName are sent — never tokens or asset content.
 * Re-identifying the same uid is skipped to avoid redundant calls on token
 * refreshes (which also update the store's `user`).
 */
export function useAnalyticsIdentity(): void {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);

  // Last uid we identified, so a token refresh (new `user` object, same uid)
  // doesn't re-identify.
  const identifiedUid = useRef<string | null>(null);

  useEffect(() => {
    if (status === 'authenticated' && user) {
      if (identifiedUid.current === user.uid) return;
      identifiedUid.current = user.uid;
      identifyUser(user.uid, {
        email: user.email ?? undefined,
        displayName: user.displayName ?? undefined,
      });
      return;
    }

    if (status === 'unauthenticated') {
      if (identifiedUid.current === null) return;
      identifiedUid.current = null;
      resetAnalytics();
    }
  }, [status, user]);
}
